import { useSelector } from 'react-redux';

/**
 * Developer-level kill switch (build / deploy time).
 * Set `enabled: false` here to permanently remove a sub-feature regardless of server flags.
 */
export const callSubFeatures = [
   { name: 'group-call', enabled: true },
   { name: 'callInfoAction', enabled: false },
   { name: 'calendarSceduleMeeting', enabled: true },
   { name: 'participantPin', enabled: true },
   { name: 'participantRemove', enabled: false },
   { name: 'missedCallLog', enabled: true },
   { name: 'screenShare', enabled: true },
   { name: 'screenShareZoom', enabled: false },
   // When false (default), 1:1 calls behave like group calls: enabling video directly
   // switches the call to video on both sides, no accept/decline popup.
   // Set to true to restore the legacy switch-request/accept flow for 1:1 calls.
   { name: 'videoSwitchEnable', enabled: true },
];

/**
 * Maps each call sub-feature name to its corresponding key in `state.features`
 * (populated by SDK.getAvailableFeatures()).
 *
 * `null` means no server flag exists — the dev registry `enabled` value is used as default.
 */
export const SERVER_FEATURE_MAP = {
   screenShare: null, // no server flag — dev registry only
   'group-call': 'isGroupCallEnabled',
};

/**
 * Static check for non-React contexts (guards, utils).
 * Only checks the dev registry — does NOT read Redux.
 * Use `useCallFeature(name)` inside React components/viewmodels.
 */
export const isEnabled = name => callSubFeatures.find(f => f.name === name)?.enabled ?? false;

/**
 * React hook — checks BOTH the dev registry AND the server flags from Redux.
 *
 * Priority:
 *  1. If dev registry `enabled: false` → always false (hard off)
 *  2. If no server flag (SERVER_FEATURE_MAP[name] === null) → use dev default
 *  3. If server flag exists → use server value
 *
 * Usage:
 *   const isGroupCallEnabled = useCallFeature('group-call')
 */
export const useCallFeature = name => {
   const serverFeatures = useSelector(state => state.features);

   const devFlag = callSubFeatures.find(f => f.name === name)?.enabled ?? false;
   if (!devFlag) return false;

   const serverKey = SERVER_FEATURE_MAP[name];
   if (!serverKey) return devFlag;

   return serverFeatures[serverKey] ?? true;
};
